// src/lib/api_google_ads.ts
// Cliente para el módulo Google Ads del backend Django (Track C — C1→C7).
// Mirrors apps/google_ads/schemas.py — /admin/google-ads/**.

const DJANGO_PROXY = '/django-api';

import { ensureFreshToken } from '@/lib/tokenRefresh';

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

async function authHeaders(): Promise<Record<string, string>> {
  return {
    Authorization: `Bearer ${await getJwt()}`,
  };
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function djangoFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = await authHeaders();
  if (init?.body !== undefined && !(init?.headers && (init.headers as Record<string, string>)['Content-Type'])) { headers['Content-Type'] = 'application/json'; }
  const url = `${DJANGO_PROXY}${path}`;
  const res = await fetch(url, { ...init, headers: { ...headers, ...(init?.headers || {}) } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail || `Django ${res.status}: ${res.statusText}`;
    if (res.status === 401) throw new ApiError('Sesión expirada.', 401);
    if (res.status === 403) throw new ApiError(detail || 'Sin permisos.', 403);
    if (res.status === 404) throw new ApiError(detail || 'Recurso no encontrado.', 404);
    if (res.status === 409) throw new ApiError(detail || 'Conflicto.', 409);
    if (res.status === 422) throw new ApiError(detail || 'Error de validación.', 422);
    throw new ApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export interface Paginated<T> { items: T[]; count: number }

// ═══════════════════════════════════════════════════════════════
// OAuth (C2) + Health (C3)
// ═══════════════════════════════════════════════════════════════

export interface OAuthStartOut { authorization_url: string; state: string }
export interface OAuthStatus {
  connected: boolean;
  google_customer_id: string;
  login_customer_id: string;
  scopes: string;
  refresh_token_set: boolean;
  connected_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  error_message: string;
}
export interface HealthOut {
  ok: boolean;
  accessible_customers: string[];
  error: string;
  status: number | null;
}

export async function getAuthStatus(): Promise<OAuthStatus> {
  return djangoFetch<OAuthStatus>('/api/v1/admin/google-ads/auth');
}

export async function startOAuth(): Promise<OAuthStartOut> {
  return djangoFetch<OAuthStartOut>(
    '/api/v1/admin/google-ads/oauth/start',
    { method: 'POST', body: '{}' },
  );
}

export async function exchangeOAuth(
  code: string, state: string,
  google_customer_id = '', login_customer_id = '',
): Promise<OAuthStatus> {
  return djangoFetch<OAuthStatus>(
    '/api/v1/admin/google-ads/oauth/exchange',
    {
      method: 'POST',
      body: JSON.stringify({ code, state, google_customer_id, login_customer_id }),
    },
  );
}

export async function updateAuth(
  patch: { google_customer_id?: string; login_customer_id?: string },
): Promise<OAuthStatus> {
  return djangoFetch<OAuthStatus>(
    '/api/v1/admin/google-ads/auth',
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
}

export async function disconnectAuth(): Promise<{ revoked: boolean; detail: string }> {
  return djangoFetch('/api/v1/admin/google-ads/auth/disconnect', {
    method: 'POST', body: '{}',
  });
}

export async function runHealthCheck(): Promise<HealthOut> {
  return djangoFetch<HealthOut>('/api/v1/admin/google-ads/health');
}

// ═══════════════════════════════════════════════════════════════
// Campaigns (C4)
// ═══════════════════════════════════════════════════════════════

export type CampaignStatus =
  | 'draft' | 'pending' | 'enabled' | 'paused' | 'removed' | 'error';
export type CampaignType =
  | 'SEARCH' | 'DISPLAY' | 'SHOPPING' | 'VIDEO' | 'MULTI_CHANNEL'
  | 'SMART' | 'PERFORMANCE_MAX' | 'DEMAND_GEN' | 'LOCAL' | 'HOTEL';
export type BiddingStrategy =
  | 'MANUAL_CPC' | 'MANUAL_CPM' | 'MAXIMIZE_CLICKS'
  | 'MAXIMIZE_CONVERSIONS' | 'MAXIMIZE_CONVERSION_VALUE'
  | 'TARGET_CPA' | 'TARGET_ROAS' | 'TARGET_IMPRESSION_SHARE';

export interface Campaign {
  id: number;
  org_id: string;
  google_customer_id: string;
  google_campaign_id: string;
  name: string;
  campaign_type: CampaignType;
  status: CampaignStatus;
  bidding_strategy_type: BiddingStrategy;
  target_cpa_micros: number | null;
  target_roas: number | null;
  daily_budget_micros: number | null;
  start_date: string | null;
  end_date: string | null;
  pushed_at: string | null;
  synced_at: string | null;
  error_message: string;
  context_data: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface CampaignIn {
  name: string;
  campaign_type: CampaignType;
  google_customer_id: string;
  bidding_strategy_type?: BiddingStrategy;
  target_cpa_micros?: number | null;
  target_roas?: number | null;
  daily_budget_micros?: number | null;
  start_date?: string | null;
  end_date?: string | null;
}

export interface CampaignActionOut {
  campaign: Campaign;
  status: 'queued' | 'synced' | 'skipped' | 'error';
  task_id?: string | null;
  detail?: string;
}

export async function listCampaigns(
  filters: {
    status?: CampaignStatus; campaign_type?: CampaignType;
    customer_id?: string; q?: string;
    limit?: number; offset?: number;
  } = {},
): Promise<Paginated<Campaign>> {
  const { limit = 50, offset = 0, ...rest } = filters;
  return djangoFetch<Paginated<Campaign>>(
    `/api/v1/admin/google-ads/campaigns${qs({ ...rest, limit, offset })}`,
  );
}

export async function getCampaign(id: number): Promise<Campaign> {
  return djangoFetch<Campaign>(`/api/v1/admin/google-ads/campaigns/${id}`);
}

export async function createCampaign(data: CampaignIn): Promise<Campaign> {
  return djangoFetch<Campaign>('/api/v1/admin/google-ads/campaigns', {
    method: 'POST', body: JSON.stringify(data),
  });
}

export async function updateCampaign(
  id: number, data: Partial<CampaignIn>,
): Promise<Campaign> {
  return djangoFetch<Campaign>(`/api/v1/admin/google-ads/campaigns/${id}`, {
    method: 'PATCH', body: JSON.stringify(data),
  });
}

export async function deleteCampaign(id: number): Promise<void> {
  await djangoFetch<void>(`/api/v1/admin/google-ads/campaigns/${id}`, { method: 'DELETE' });
}

export async function pushCampaign(id: number): Promise<CampaignActionOut> {
  return djangoFetch<CampaignActionOut>(
    `/api/v1/admin/google-ads/campaigns/${id}/push`,
    { method: 'POST', body: '{}' },
  );
}

export async function syncCampaigns(): Promise<{ org_id: string; task_id: string; status: string }> {
  return djangoFetch(
    '/api/v1/admin/google-ads/campaigns/sync',
    { method: 'POST', body: '{}' },
  );
}

// ── Actions (C7) ────────────────────────────────────────────

export async function pauseCampaign(id: number): Promise<Campaign> {
  return djangoFetch<Campaign>(
    `/api/v1/admin/google-ads/campaigns/${id}/pause`,
    { method: 'POST', body: '{}' },
  );
}

export async function resumeCampaign(id: number): Promise<Campaign> {
  return djangoFetch<Campaign>(
    `/api/v1/admin/google-ads/campaigns/${id}/resume`,
    { method: 'POST', body: '{}' },
  );
}

export async function adjustCampaignBudget(
  id: number, daily_budget_micros: number,
): Promise<Campaign> {
  return djangoFetch<Campaign>(
    `/api/v1/admin/google-ads/campaigns/${id}/budget`,
    { method: 'PATCH', body: JSON.stringify({ daily_budget_micros }) },
  );
}

// ═══════════════════════════════════════════════════════════════
// Ad groups (C5)
// ═══════════════════════════════════════════════════════════════

export interface AdGroup {
  id: number;
  campaign_id: number;
  google_ad_group_id: string;
  name: string;
  status: CampaignStatus;
  cpc_bid_micros: number | null;
  cpm_bid_micros: number | null;
  target_cpa_micros: number | null;
  pushed_at: string | null;
  synced_at: string | null;
  error_message: string;
  created_at: string;
  updated_at: string;
}

export async function listAdGroups(
  filters: { campaign_id?: number; status?: string; limit?: number; offset?: number } = {},
): Promise<Paginated<AdGroup>> {
  const { limit = 50, offset = 0, ...rest } = filters;
  return djangoFetch<Paginated<AdGroup>>(
    `/api/v1/admin/google-ads/ad-groups${qs({ ...rest, limit, offset })}`,
  );
}

export async function createAdGroup(data: {
  campaign_id: number;
  name: string;
  cpc_bid_micros?: number | null;
  cpm_bid_micros?: number | null;
  target_cpa_micros?: number | null;
}): Promise<AdGroup> {
  return djangoFetch<AdGroup>('/api/v1/admin/google-ads/ad-groups', {
    method: 'POST', body: JSON.stringify(data),
  });
}

export async function pushAdGroup(id: number): Promise<{ status: string; task_id: string }> {
  return djangoFetch(
    `/api/v1/admin/google-ads/ad-groups/${id}/push`,
    { method: 'POST', body: '{}' },
  );
}

// ═══════════════════════════════════════════════════════════════
// Ads (C5)
// ═══════════════════════════════════════════════════════════════

export interface Ad {
  id: number;
  ad_group_id: number;
  google_ad_id: string;
  ad_type: string;
  status: CampaignStatus;
  headlines: Array<{ text: string; pinned?: number } | string>;
  descriptions: Array<{ text: string } | string>;
  final_urls: string[];
  path1: string;
  path2: string;
  pushed_at: string | null;
  error_message: string;
  created_at: string;
  updated_at: string;
}

export async function listAds(
  filters: { ad_group_id?: number; limit?: number; offset?: number } = {},
): Promise<Paginated<Ad>> {
  const { limit = 50, offset = 0, ...rest } = filters;
  return djangoFetch<Paginated<Ad>>(
    `/api/v1/admin/google-ads/ads${qs({ ...rest, limit, offset })}`,
  );
}

export async function createAd(data: {
  ad_group_id: number;
  ad_type?: string;
  headlines: Array<{ text: string; pinned?: number } | string>;
  descriptions: Array<{ text: string } | string>;
  final_urls: string[];
  path1?: string;
  path2?: string;
}): Promise<Ad> {
  return djangoFetch<Ad>('/api/v1/admin/google-ads/ads', {
    method: 'POST', body: JSON.stringify(data),
  });
}

export async function pushAd(id: number): Promise<{ status: string; task_id: string }> {
  return djangoFetch(
    `/api/v1/admin/google-ads/ads/${id}/push`,
    { method: 'POST', body: '{}' },
  );
}

// ═══════════════════════════════════════════════════════════════
// Keywords (C5)
// ═══════════════════════════════════════════════════════════════

export type KeywordMatchType = 'EXACT' | 'PHRASE' | 'BROAD';

export interface Keyword {
  id: number;
  ad_group_id: number;
  google_criterion_id: string;
  text: string;
  match_type: KeywordMatchType;
  status: CampaignStatus;
  cpc_bid_micros: number | null;
  pushed_at: string | null;
  error_message: string;
  created_at: string;
  updated_at: string;
}

export async function listKeywords(
  filters: { ad_group_id?: number; q?: string; limit?: number; offset?: number } = {},
): Promise<Paginated<Keyword>> {
  const { limit = 100, offset = 0, ...rest } = filters;
  return djangoFetch<Paginated<Keyword>>(
    `/api/v1/admin/google-ads/keywords${qs({ ...rest, limit, offset })}`,
  );
}

export async function createKeyword(data: {
  ad_group_id: number;
  text: string;
  match_type?: KeywordMatchType;
  cpc_bid_micros?: number | null;
}): Promise<Keyword> {
  return djangoFetch<Keyword>('/api/v1/admin/google-ads/keywords', {
    method: 'POST', body: JSON.stringify(data),
  });
}

export async function pushKeyword(id: number): Promise<{ status: string; task_id: string }> {
  return djangoFetch(
    `/api/v1/admin/google-ads/keywords/${id}/push`,
    { method: 'POST', body: '{}' },
  );
}

// ═══════════════════════════════════════════════════════════════
// Metrics (C6)
// ═══════════════════════════════════════════════════════════════

export interface MetricsDaily {
  date: string;
  impressions: number;
  clicks: number;
  cost_micros: number;
  conversions: number;
  conversion_value: number;
  ctr: number;
  average_cpc_micros: number | null;
}

export interface MetricsSummary {
  entity_id: number;
  entity_type: string;
  window_start: string;
  window_end: string;
  totals: MetricsDaily;
  daily: MetricsDaily[];
}

export async function getCampaignMetrics(id: number, days = 7): Promise<MetricsSummary> {
  return djangoFetch<MetricsSummary>(
    `/api/v1/admin/google-ads/campaigns/${id}/metrics${qs({ days })}`,
  );
}

export async function triggerMetricsSync(days = 7): Promise<{ org_id: string; task_id: string; status: string; detail: string }> {
  return djangoFetch(
    `/api/v1/admin/google-ads/campaigns/metrics/sync${qs({ days })}`,
    { method: 'POST', body: '{}' },
  );
}

// ═══════════════════════════════════════════════════════════════
// Frontend convenience
// ═══════════════════════════════════════════════════════════════

/** Convert integer micros → display currency string (MXN default). */
export function microsToCurrency(micros: number | null, locale = 'es-MX', currency = 'MXN'): string {
  if (micros == null) return '—';
  const value = micros / 1_000_000;
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value);
}

/** Currency string → micros. Strips non-digit/decimal chars, multiplies. */
export function currencyToMicros(text: string): number | null {
  if (!text) return null;
  const cleaned = text.replace(/[^0-9.,-]/g, '').replace(',', '.');
  const num = parseFloat(cleaned);
  if (!Number.isFinite(num)) return null;
  return Math.round(num * 1_000_000);
}

/** "customers/1234567890" → "1234567890". Passes through bare IDs. */
export function customerIdFromResourceName(rn: string): string {
  if (!rn) return '';
  const parts = rn.split('/');
  return parts[parts.length - 1];
}
